/**
 * The kitbash vocabulary (U15, R24).
 *
 * A small set of primitives — box, wedge, cylinder, plate, tapered extrusion,
 * tread band, panel array — composed by per-entity recipes. A tank is a hull
 * wedge, a turret cylinder, a barrel, and two tread bands. Recipes are
 * data-adjacent code, so adding a unit's visual is bounded work that never
 * touches the renderer.
 *
 * **Meshes are authored as rigid sub-parts**, because KTD2's batching draws one
 * `InstancedMesh` per entity type per sub-part. That decision shapes every
 * recipe, so it is settled here rather than discovered during an optimization
 * pass — a mesh built as one welded blob cannot have an animated turret without
 * being rebuilt from scratch.
 *
 * Keep the effort proportionate: at roughly 24 pixels per cell with a
 * 48-colour palette,
 * high-frequency detail is destroyed by quantization. Invest in **silhouette**,
 * palette assignment, and readable animation. A unit must be identifiable by
 * silhouette alone at gameplay zoom — the constraint Red Alert designed to.
 */

import { BufferAttribute, BufferGeometry } from 'three'
import type { RigidWalk } from './rigid-walk.js'

/** Which rigid sub-part a piece of geometry belongs to. */
export const PART = {
  /** Static body. Rotates with the unit's heading. */
  Hull: 0,
  /** Rotates independently with the turret heading. */
  Turret: 1,
  /** Scrolls with movement — treads, legs, rotor. */
  Tread: 2,
  /** Never rotates. Antennae, banners, hover glow. */
  Accent: 3,
  /**
   * A round of ordnance hanging on a pylon. Drawn only while the unit still
   * has that round: an aircraft's remaining payload is the one piece of state
   * a player has to be able to read at a glance, because it decides whether
   * the thing is a threat or a target.
   */
  Ordnance0: 4,
  Ordnance1: 5,
  Ordnance2: 6,
  /**
   * Fieldworks: the sandbags a squad throws up when it digs in. Drawn only
   * while `EF.DugIn` is set, the same way a spent pylon is hidden.
   */
  Works: 7,
  /**
   * A transport's ring-mount fittings, drawn by who is riding (U15's
   * passenger-is-the-weapon made visible). Exactly one shows at a time, the
   * same scale-to-zero way a spent pylon hides: the chassis stub when empty,
   * the twin gun for a small-arms passenger, the long barrel for a marksman,
   * the crane for the engineer. All rotate with the turret.
   */
  MountStub: 8,
  MountGun: 9,
  MountLong: 10,
  MountCrane: 11,
  /** Alternating legs, or diagonal leg pairs on quadrupeds. */
  LegA: 12,
  LegB: 13,
} as const

/** Ordnance parts in order, so a renderer can map round `k` to a part kind. */
export const ORDNANCE_PARTS = [4, 5, 6] as const

/** Parts that track the turret heading rather than the hull's. */
export const TURRET_PARTS: readonly number[] = [
  PART.Turret,
  PART.MountStub,
  PART.MountGun,
  PART.MountLong,
  PART.MountCrane,
]

/**
 * Which ring-mount part a transport shows for a passenger kind, or the stub
 * for nobody. Keyed on what the passenger *is*, not its exact model: the
 * marksman's silhouette is the barrel, the engineer's is the crane, and
 * everything else that shoots reads as the twin gun.
 */
export function mountPartFor(passenger: {
  isEngineer?: boolean
  key?: string
} | undefined): PartKind {
  if (!passenger) return PART.MountStub
  if (passenger.isEngineer) return PART.MountCrane
  if (passenger.key === 'marksman' || passenger.key === 'nullifier') return PART.MountLong
  return PART.MountGun
}

export type PartKind = (typeof PART)[keyof typeof PART]

/** A palette role, resolved against the owner ramp or a terrain ramp at build. */
export const SHADE = {
  /** The owner's ramp — this is what makes a unit read as yours. */
  Owner: 0,
  OwnerDark: 1,
  OwnerLight: 2,
  /** Neutral greys for machinery that is not team-coloured. */
  Metal: 3,
  MetalDark: 4,
  /** Emissive accents: barrels, vents, glass. */
  Accent: 5,
  /**
   * The middle step of the neutral chassis ramp.
   *
   * `Metal` and `MetalDark` are the two ends of it — a light deck and a dark
   * undercarriage — and with only those two a hull painted in bare alloy jumps
   * straight from one to the other. The reference frames put most of a
   * vehicle's *body* on the step between: a mid neutral mass carrying the team
   * colour as trim, rather than a unit that is team colour with grey fittings.
   * Without this step every recipe that wanted a neutral body had to reach for
   * the owner ramp, which is how a whole faction ended up one hue.
   */
  MetalMid: 6,
  Glass: 7,
  Light: 8,
} as const

export type ShadeRole = (typeof SHADE)[keyof typeof SHADE]

/** A mesh under construction. Positions, normals, and a per-vertex shade role. */
export class MeshBuilder {
  private readonly positions: number[] = []
  private readonly normals: number[] = []
  private readonly shades: number[] = []
  private readonly indices: number[] = []
  private readonly panels: number[] = []

  get triangleCount(): number {
    return this.indices.length / 3
  }

  /** Emit a quad with a flat normal. Winding is counter-clockwise from outside. */
  quad(
    a: readonly number[],
    b: readonly number[],
    c: readonly number[],
    d: readonly number[],
    shade: ShadeRole,
  ): this {
    const nx = normalOf(a, b, c)
    const base = this.positions.length / 3
    const width = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])
    const height = Math.hypot(d[0] - a[0], d[1] - a[1], d[2] - a[2])
    for (const [u, v] of [[0, 0], [width, 0], [width, height], [0, height]]) {
      this.panels.push(u, v, width, height)
    }
    for (const v of [a, b, c, d]) {
      this.positions.push(v[0], v[1], v[2])
      this.normals.push(nx[0], nx[1], nx[2])
      this.shades.push(shade)
    }
    this.indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
    return this
  }

  tri(a: readonly number[], b: readonly number[], c: readonly number[], shade: ShadeRole): this {
    const n = normalOf(a, b, c)
    const base = this.positions.length / 3
    for (const v of [a, b, c]) {
      this.positions.push(v[0], v[1], v[2])
      this.normals.push(n[0], n[1], n[2])
      this.shades.push(shade)
      this.panels.push(0, 0, 0, 0)
    }
    this.indices.push(base, base + 1, base + 2)
    return this
  }

  /**
   * An axis-aligned box. `y` runs from `y0` up to `y0 + h`, so a part sits on
   * the ground by default rather than being centred through it.
   */
  box(
    cx: number,
    y0: number,
    cz: number,
    w: number,
    h: number,
    d: number,
    shade: ShadeRole,
    topShade = shade,
  ): this {
    const x0 = cx - w / 2
    const x1 = cx + w / 2
    const z0 = cz - d / 2
    const z1 = cz + d / 2
    const y1 = y0 + h

    // Wound from +z to -z so the normal points **up**. The mirror ordering
    // reads naturally and produces a downward normal, which face culling then
    // removes — every roof in the game was silently invisible.
    this.quad([x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0], topShade) // top
    this.quad([x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], shade) // +z
    this.quad([x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], shade) // -z
    this.quad([x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], shade) // +x
    this.quad([x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], shade) // -x
    return this
  }

  /**
   * A box whose top face is inset — the workhorse of vehicle hulls and
   * structure roofs. `inset` of 0 is a plain box; larger values taper.
   */
  wedge(
    cx: number,
    y0: number,
    cz: number,
    w: number,
    h: number,
    d: number,
    inset: number,
    shade: ShadeRole,
    topShade = shade,
  ): this {
    const x0 = cx - w / 2
    const x1 = cx + w / 2
    const z0 = cz - d / 2
    const z1 = cz + d / 2
    const tx0 = x0 + inset
    const tx1 = x1 - inset
    const tz0 = z0 + inset
    const tz1 = z1 - inset
    const y1 = y0 + h

    this.quad([tx0, y1, tz1], [tx1, y1, tz1], [tx1, y1, tz0], [tx0, y1, tz0], topShade)
    this.quad([x0, y0, z1], [x1, y0, z1], [tx1, y1, tz1], [tx0, y1, tz1], shade)
    this.quad([x1, y0, z0], [x0, y0, z0], [tx0, y1, tz0], [tx1, y1, tz0], shade)
    this.quad([x1, y0, z1], [x1, y0, z0], [tx1, y1, tz0], [tx1, y1, tz1], shade)
    this.quad([x0, y0, z0], [x0, y0, z1], [tx0, y1, tz1], [tx0, y1, tz0], shade)
    return this
  }

  /**
   * A prism approximating a cylinder. Six or eight sides is plenty: the
   * quantizer erases anything smoother, and the triangle budget is better
   * spent on a second sub-part than on a rounder one.
   */
  cylinder(
    cx: number,
    y0: number,
    cz: number,
    radius: number,
    h: number,
    sides: number,
    shade: ShadeRole,
    topShade = shade,
    topRadius = radius,
  ): this {
    const ring: number[][] = []
    const topRing: number[][] = []
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2
      ring.push([cx + Math.cos(a) * radius, y0, cz + Math.sin(a) * radius])
      topRing.push([cx + Math.cos(a) * topRadius, y0 + h, cz + Math.sin(a) * topRadius])
    }
    for (let i = 0; i < sides; i++) {
      const j = (i + 1) % sides
      this.quad(ring[i], ring[j], topRing[j], topRing[i], shade)
    }
    // Fan the cap, wound so its normal points up for the same reason.
    for (let i = 1; i < sides - 1; i++) {
      this.tri(topRing[0], topRing[i + 1], topRing[i], topShade)
    }
    return this
  }

  /** A thin horizontal plate. Roof panels, solar arrays, skirts. */
  plate(cx: number, y: number, cz: number, w: number, d: number, shade: ShadeRole): this {
    return this.box(cx, y, cz, w, 0.05, d, shade)
  }

  /**
   * Panel seams across a flat top face, dividing it into plates.
   *
   * The largest surfaces in the game are horizontal — a landing pad, a
   * refinery deck, a tank's engine cover — and a fixed overhead camera means
   * they are also most of what is ever seen of those objects. Left plain they
   * are the only thing on screen with nothing in them, which is what "the
   * models still look simplistic" comes down to: not too few parts, but a few
   * large parts with unbroken faces.
   *
   * Seams are raised rather than recessed. A groove would be the truthful way
   * to draw a panel join and it is invisible here, because the surface it is
   * cut into is lit flatly and a recess in a flat-lit surface has nothing to
   * shade it. A proud strip catches its own light and throws its own thin
   * shadow, which is what makes it read at eight pixels.
   */
  seams(
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    cols: number,
    rows: number,
    shade: ShadeRole = SHADE.MetalDark,
  ): this {
    const t = 0.045
    for (let i = 1; i < cols; i++) {
      this.box(cx - w / 2 + (w * i) / cols, y, cz, t, 0.025, d, shade)
    }
    for (let i = 1; i < rows; i++) {
      this.box(cx, y, cz - d / 2 + (d * i) / rows, w, 0.025, t, shade)
    }
    return this
  }

  /** Studs inset from the four corners of a rectangle. Bolt heads, tie-downs. */
  studs(cx: number, y: number, cz: number, w: number, d: number, shade: ShadeRole): this {
    const ix = w / 2 - 0.1
    const iz = d / 2 - 0.1
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        this.box(cx + sx * ix, y, cz + sz * iz, 0.08, 0.05, 0.08, shade)
      }
    }
    return this
  }

  /**
   * A guard rail around a deck: corner posts joined by a top rail.
   *
   * Unlike a seam this changes the *silhouette*, which is the one kind of
   * detail that survives all the way out to gameplay zoom. A flat deck with a
   * rail around it reads as a structure people work on; the same deck without
   * one reads as a coloured tile.
   */
  railing(
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    h: number,
    shade: ShadeRole = SHADE.Metal,
  ): this {
    const post = 0.07
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        this.box(cx + (sx * w) / 2, y, cz + (sz * d) / 2, post, h, post, shade)
      }
    }
    for (const sz of [-1, 1]) {
      this.box(cx, y + h - 0.05, cz + (sz * d) / 2, w, 0.05, post * 0.7, shade)
    }
    for (const sx of [-1, 1]) {
      this.box(cx + (sx * w) / 2, y + h - 0.05, cz, post * 0.7, 0.05, d, shade)
    }
    return this
  }

  /**
   * A tread band: a low box with a row of raised cleats. The cleats are what
   * make tread scroll legible at six pixels wide — a smooth band animated by a
   * texture would simply not read.
   *
   * The cleats take the **mid** chassis value against a dark band, and that step
   * is the whole point of them. Cleat and band were both the darkest neutral, so
   * the running gear resolved as one unbroken near-black rectangle running the
   * length of the vehicle and past both ends of it — a jury read it as a black
   * base slab the hull was sitting on rather than as track, and on the wider
   * chassis it swallowed the hull's own outline. A ribbed band with a value
   * change every cleat separates track from hull in silhouette, which is what a
   * fixed overhead camera needs from running gear.
   */
  treadBand(cx: number, cz: number, length: number, width: number, shade: ShadeRole): this {
    this.box(cx, 0, cz, width, 0.13, length, shade)
    const cleats = Math.max(3, Math.round(length / 0.12))
    for (let i = 0; i < cleats; i++) {
      const z = cz - length / 2 + (i + 0.5) * (length / cleats)
      // Across the band's *flank*, not along its top. The top of a track is
      // under the vehicle's own side skirt on every recipe that has one, so
      // cleats emitted there were geometry nothing could ever see, and the only
      // face of the running gear the camera actually gets — the outer one — was
      // an unbroken near-black rectangle longer than the hull.
      this.box(cx, 0.042, z, width * 1.22, 0.042, length / cleats / 2, SHADE.MetalMid)
    }
    return this
  }

  /**
   * A row of panels on a frame — solar arrays, radiator banks, antenna masts.
   * The single most useful structure primitive, because a repeated element at
   * this resolution reads as "industrial" more strongly than any texture.
   */
  panelArray(
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    count: number,
    tilt: number,
    shade: ShadeRole,
  ): this {
    const step = d / count
    for (let i = 0; i < count; i++) {
      const z = cz - d / 2 + (i + 0.5) * step
      const lift = tilt * (i / Math.max(1, count - 1) - 0.5)
      this.box(cx, y + lift, z, w, 0.06, step * 0.75, shade)
    }
    return this
  }

  /**
   * Merge another builder's geometry, offset, optionally scaled, and optionally
   * turned about its own Y axis.
   *
   * The yaw exists for figures. Every primitive here is axis-aligned, so three
   * troopers built from the same blocks stand in exactly the same direction and
   * a squad reads as one man stamped three times — which is precisely what a
   * jury counted. A few degrees each way is the cheapest pose variation there
   * is, and it costs no triangles.
   */
  merge(other: MeshBuilder, dx = 0, dy = 0, dz = 0, scale = 1, yaw = 0): this {
    const base = this.positions.length / 3
    const c = Math.cos(yaw)
    const s = Math.sin(yaw)
    for (let i = 0; i < other.positions.length; i += 3) {
      const x = other.positions[i]
      const z = other.positions[i + 2]
      this.positions.push(
        (x * c + z * s) * scale + dx,
        other.positions[i + 1] * scale + dy,
        (z * c - x * s) * scale + dz,
      )
      const nx = other.normals[i]
      const nz = other.normals[i + 2]
      this.normals.push(nx * c + nz * s, other.normals[i + 1], nz * c - nx * s)
    }
    this.shades.push(...other.shades)
    for (const value of other.panels) this.panels.push(value * scale)
    for (const idx of other.indices) this.indices.push(idx + base)
    return this
  }

  /**
   * Finish. The `shade` attribute travels as a vertex attribute so a single
   * material can resolve palette roles per vertex — one draw call per sub-part
   * rather than one per colour.
   */
  build(): BufferGeometry {
    const g = new BufferGeometry()
    g.setAttribute('position', new BufferAttribute(Float32Array.from(this.positions), 3))
    g.setAttribute('normal', new BufferAttribute(Float32Array.from(this.normals), 3))
    g.setAttribute('shade', new BufferAttribute(Float32Array.from(this.shades), 1))
    g.setAttribute('panel', new BufferAttribute(Float32Array.from(this.panels), 4))
    g.setIndex(this.indices)
    g.computeBoundingSphere()
    return g
  }
}

function normalOf(a: readonly number[], b: readonly number[], c: readonly number[]): number[] {
  const ux = b[0] - a[0]
  const uy = b[1] - a[1]
  const uz = b[2] - a[2]
  const vx = c[0] - a[0]
  const vy = c[1] - a[1]
  const vz = c[2] - a[2]
  const nx = uy * vz - uz * vy
  const ny = uz * vx - ux * vz
  const nz = ux * vy - uy * vx
  const len = Math.hypot(nx, ny, nz) || 1
  return [nx / len, ny / len, nz / len]
}

/** A finished recipe: one builder per rigid sub-part. */
export interface MeshRecipe {
  readonly parts: ReadonlyArray<{ kind: PartKind; geometry: BufferGeometry; walk?: RigidWalk }>
  readonly copies?: readonly (readonly number[])[]
}

export class RecipeBuilder {
  private readonly parts: Array<{ kind: PartKind; builder: MeshBuilder }> = []

  part(kind: PartKind): MeshBuilder {
    const existing = this.parts.find((p) => p.kind === kind)
    if (existing) return existing.builder
    const builder = new MeshBuilder()
    this.parts.push({ kind, builder })
    return builder
  }

  build(): MeshRecipe {
    return {
      parts: this.parts.map((p) => ({ kind: p.kind, geometry: p.builder.build() })),
    }
  }

  get triangleCount(): number {
    return this.parts.reduce((n, p) => n + p.builder.triangleCount, 0)
  }
}
